<template>
    <aside class="md:w-[270px] w-full bg-white rounded-medium p-7 pt-0 md:p-4 md:py-7 flex flex-col gap-6">
        <h2 class="font-walsheim text-lg md:hidden">Order summary</h2>
        <BillingToggle
            class="hidden md:flex"
            v-if="hasBillingToggle"
            :disabled="$store.getters.isSoftLoading"
        />
        <Dropdown
            v-if="(hasCurrency && !$store.getters.isUpgrading) && !$store.getters.showContactSales"
            :disabled="$store.getters.isSoftLoading"
            key="currency-selection"
            v-model="currency"
            :value="currency"
            data-qa="currency-selection"
            field-label="Currency"
            hide-label
            :valid="true"
            :options="currencies"
        >
        </Dropdown>

        <div v-if="!$store.getters.showContactSales">
            <div class="line-item-group">
                <LineItem
                    :item="planItem"
                    :data-qa="planItem.id"
                    :is-attachment="false"
                />
                <LineItem
                    v-for="item in addonLineItems"
                    :key="item.id"
                    :data-qa="item.itemId"
                    :item="item"
                    :is-attachment="true"
                />
                <div class="line-item" data-qa="subtotal">
                    <div class="line-item__header">
                        <h3><span v-text="$store.getters.isYearly ? 'Annual' : 'Monthly'"></span> subtotal</h3>
                        <p data-qa="subtotal-total"><span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.totals.subTotal) }}/<span v-text="$store.getters.isYearly ? 'yr' : 'mo'"></span></p>
                    </div>
                </div>
            </div>

            <div class="line-item" v-if="invoiceCoupons.length > 0">
                <div class="line-item__header">
                    <h3>Invoice discounts</h3>
                </div>
                <div class="line-item__items">
                    <ul>
                        <li class="line-item__item line-item__item--discount" v-for="coupon in invoiceCoupons" :key="coupon.id">
                            <span>
                                {{ coupon.description }}
                            </span>
                            <span>
                               -<span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat(coupon.calcAmount) }}/<span v-text="$store.getters.isYearly ? 'yr' : 'mo'"></span>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="line-item-group" v-if="chargeLineItems.length > 0">
                <LineItem
                    v-for="item in chargeLineItems"
                    :key="item.id"
                    :data-qa="item.itemId"
                    :item="item"
                    :is-attachment="true"
                >
                    <template v-slot:default v-if="item.customDescription">
                        <div class="inline" v-html="item.customDescription"></div>
                    </template>
                </LineItem>
            </div>

            <div class="line-item-group" v-if="!!$store.getters.cartInfo.flags?.SHOW_ALL_PROMOS">
                <div class="line-item">
                    <div class="line-item__header">
                        <h3>Promotions</h3>
                    </div>
                    <div class="line-item__items">
                        <ul>
                            <li class="line-item__item line-item__item--discount" v-if="$store.getters.totals.limitedPeriodDiscountTotal + $store.getters.totals.invoiceDiscountTotal.limitedPeriodTotal > 0" data-qa="recurring-promos" >
                                <span data-qa="recurring-promos-type">
                                    {{ $store.getters.isYearly ? 'Yearly' : 'Monthly' }} promotions
                                </span>
                                <span data-qa="recurring-promos-total">
                                -<span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.totals.limitedPeriodDiscountTotal + $store.getters.totals.invoiceDiscountTotal.limitedPeriodTotal) }}/<span v-text="$store.getters.isYearly ? 'yr' : 'mo'"></span>
                                </span>
                            </li>
                            <li class="line-item__item line-item__item--discount" v-if="$store.getters.totals.oneTimeDiscountTotal + $store.getters.totals.invoiceDiscountTotal.oneTimeTotal > 0" data-qa="one-time-promos" >
                                <span>
                                    One-time promotions
                                </span>
                                <span data-qa="one-time-promos-total">
                                -<span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.totals.oneTimeDiscountTotal + $store.getters.totals.invoiceDiscountTotal.oneTimeTotal) }}
                                </span>
                            </li>
                            <li class="line-item__item line-item__item--discount" v-if="$store.getters.totals.everGreenDiscountTotal + $store.getters.totals.invoiceDiscountTotal.everGreenTotal > 0" data-qa="evergreen-promos" >
                                <span>
                                    Forever promotions
                                </span>
                                <span data-qa="evergreen-promos-total">
                                -<span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.totals.everGreenDiscountTotal + $store.getters.totals.invoiceDiscountTotal.everGreenTotal) }}/<span v-text="$store.getters.isYearly ? 'yr' : 'mo'"></span>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="line-item-group">
                <div class="line-item" data-qa="totalDue">
                    <div class="line-item__header">
                        <h3>Subtotal due</h3>
                        <p data-qa="total-due-total">{{ $store.getters.currencyPrefix }}{{ subtotalInvoiceDue }}</p>
                    </div>
                </div>
                <div class="line-item" data-qa="totalDue" v-if="!!appliedPromos">
                    <div class="line-item__header">
                        <h3 class="text-green-accessible">Promotions</h3>
                        <p class="text-green-accessible" data-qa="total-due-total">{{ appliedPromos }}</p>
                    </div>
                </div>
                <div class="line-item" v-if="$store.getters.tax > 0" data-qa="tax">
                    <div class="line-item__header">
                        <h3>Tax</h3>
                        <p data-qa="tax-total"><span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.tax) }}</p>
                    </div>
                </div>
                <div class="line-item" data-qa="totalDue">
                    <div class="line-item__header">
                        <h3>Total due</h3>
                        <p data-qa="total-due-total">{{ $store.getters.currencyPrefix }}{{ totalInvoiceDue }}</p>
                    </div>
                </div>
                <div class="line-item" data-qa="totalDue" v-if="$store.getters.cartInfo.installments.enabled">
                    <div class="line-item__header">
                        <h3>Installments</h3>
                        <p data-qa="total-due-total">{{ $store.getters.currencyPrefix }}{{ chargedTodayTotal }} x {{ $store.getters.cartInfo.installments.count }}</p>
                    </div>
                </div>
            </div>

            <div class="line-item-group" v-if="$store.getters.invalidCoupons.length > 0 && $store.getters.isCuratedLink">
                <div class="line-item">
                    <div class="line-item__header">
                        <h3>Invalid promotions</h3>
                    </div>
                    <div class="line-item__items">
                        <ul>
                            <li class="line-item__item line-item__item--invalid-discount" v-for="coupon in $store.getters.invalidCoupons" :key="coupon">
                                {{ coupon }}
                            </li>
                        </ul>
                        <p v-if="$store.getters.isPartnerLink && !$store.getters.isPurchaseLink">Please contact your partner for assistance.</p>
                        <p v-if="!$store.getters.isPartnerLink && $store.getters.isPurchaseLink">Please contact your sales rep for assistance.</p>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="hasChargeToday && !$store.getters.showContactSales" class="text-sm font-medium text-center">
            You’ll be charged<br/>
            <span data-qa="charged-today-total">{{ $store.getters.currencyPrefix }}{{ chargedTodayTotal }}</span> today
        </div>
        <p v-if="hasLegal && $store.getters.cartInfo.installments.enabled" class="text-2xs">
            You are agreeing to an annual contract with {{ $store.getters.cartInfo.installments.count }} payments of {{ $store.getters.currencyPrefix }}{{ chargedTodayTotal }}/each payable in month {{ installmentBreakdown }} of your subscription, plus tax. Until you cancel, your plan will automatically renew annually at the then-current price for all services you are using. We will notify you in advance of any price changes. Please contact customer service at <a href="tel:18668000004" class="underline">+1 (866) 800-0004</a> to adjust or cancel your plan. No refunds apply.
        </p>
        <p v-if="hasLegal && !$store.getters.showContactSales && !$store.getters.cartInfo.installments.enabled" class="text-2xs">
            <span v-if="!$store.getters.isPartnerLink">By providing a credit or debit card, you authorize us to charge it for outstanding invoices and automatically at the start of each billing period for recurring Thryv service fees, plus taxes and minus any credits, until you cancel in accordance with the <a href="https://www.thryv.com/keap-terms-conditions/"  class="underline" target="_blank">Terms and Conditions</a>. See Sections 6–8 for details.</span>
            <span v-else>Your estimated plan renewal amount will be <span data-qa="after-promo-total"><span v-text="$store.getters.currencyPrefix"></span><span v-text="renewalTotal"></span>/<span v-text="$store.getters.isYearly ? 'yr' : 'mo'"></span></span>, plus tax and not including any limited time promotions. Until you cancel, your plan will automatically renew <span v-text="$store.getters.isYearly ? 'annually' : 'monthly'"></span> at the then-current price for all services you are using. We will notify you in advance of any price changes. Please contact customer service at <a href="tel:18668000004" class="underline">+1 (866) 800-0004</a> to adjust or cancel your plan. No refunds apply.</span>
        </p>
        <div v-if="hasLegal && $store.getters.isMonthly && $store.getters.purchaseInfo.showEtf && !$store.getters.showContactSales" data-qa="etf-legal">
            <h3 class="text-red-default font-bold text-2xs text-center mb-3">
                <span v-if="!$store.getters.isPartnerLink">The initial Subscription Period is 6 months, or 12 months if you chose an annual prepaid plan.</span>
                <span v-else>You are agreeing to a 12-month<br/>contract term.</span>
            </h3>
            <p class="text-2xs text-red-default">
                You will be charged $299 for early termination if you cancel your subscription prior to 1 year. This fee also applies if Thryv terminates your subscription for cause, such as violating our <a href="https://keap.com/legal/aup" target="_blank">Acceptable Use Policy</a>.
            </p>
        </div>
        <div v-if="hasLegal && $store.getters.purchaseInfo.showMbg90 && !$store.getters.showContactSales" class="text-2xs" data-qa="tc-legal">
            <CheckBox
                key="mbg90Agreement"
                v-model="mbg90Agreement"
                :value="mbg90Agreement"
                data-qa="tc-checkbox"
                :valid="mbg90AgreementValid"
                field-type="text"
                required
                class="col-span-2 md:col-span-1"
            >
                <template v-slot:label>
                    <p class="text-2xs font-bold">Keap 90-Day Money Back Guarantee</p>
                    <p class="text-2xs" data-qa="tc-label">Click here to indicate that you have read and agree to Keap’s <a class="underline" href="https://keap.com/legal/90-day-money-back-guarantee" target="_blank">90-day Money Back Guarantee terms</a></p>
                </template>
            </CheckBox>
        </div>
        <div v-if="hasLegal && !$store.getters.showContactSales" class="text-2xs" data-qa="tc-legal">
            <CheckBox
                key="tcAgreement"
                v-model="tcAgreement"
                :value="tcAgreement"
                data-qa="tc-checkbox"
                :valid="tcAgreementValid"
                field-type="text"
                required
                class="col-span-2 md:col-span-1"
            >
                <template v-slot:label>
                    <p class="text-2xs font-bold">Thryv Customer Agreement</p>
                    <p class="text-2xs" data-qa="tc-label">Click here to indicate that you have read and agree to Thryv’s <a class="underline" href="https://keap.com/legal/terms-of-service" target="_blank">Terms and Conditions</a><span v-text="$store.getters.purchaseInfo.showTc ? ',&nbsp;' : '&nbsp;and&nbsp;'"></span><a class="underline" href="https://keap.com/legal/privacy-policy/" target="_blank">Privacy Policy</a><span v-if="$store.getters.purchaseInfo.showTc"> and the contract terms presented above.</span></p>
                </template>
            </CheckBox>
        </div>
        <div v-if="hasCheckout && !$store.getters.showContactSales">
            <KeapButton v-if="currentStep == 0 && !$store.getters.showContactSales" @click.prevent="goToStep(1)" large-expanded xs data-qa="go-to-payment-button">
                Go to payment
            </KeapButton>
            <KeapButton v-else @click.prevent="$emit('submitPayment')" large-expanded xs data-qa="pay-now-button" :disabled="!canSubmit">
                Agree and pay now
            </KeapButton>
            <!-- // ? Not sure we need this anymore since the page is smaller and less distracting -->
            <!-- <p class="text-2xs text-red-default text-center mt-2">Required Fields are missing</p> -->
        </div>
        <SupportLink />
    </aside>
</template>

<script>
import * as Analytics from '@/Util/reporting.js';

import BillingToggle from '@/BuyNow/Components/BillingToggle.vue';
import Dropdown from '@/BuyNow/Components/inputs/Dropdown.vue';
import CheckBox from '@/BuyNow/Components/inputs/CheckBox.vue';
import SupportLink from '@/BuyNow/Components/SupportLink.vue';
import KeapButton from '@/Components/KeapButton.vue';
import LineItem from '@/BuyNow/Components/Summary/LineItem.vue';
import {priceFormat, pluralize} from '@/Util/helpers.js';

export default {
    components: {
        BillingToggle,
        Dropdown,
        KeapButton,
        SupportLink,
        CheckBox,
        LineItem,
    },
    props: {
        hasBillingToggle: {
            type: Boolean,
            default: false,
        },
        hasCurrency: {
            type: Boolean,
            default: false,
        },
        hasCheckout: {
            type: Boolean,
            default: false,
        },
        hasChargeToday: {
            type: Boolean,
            default: false,
        },
        hasLegal: {
            type: Boolean,
            default: false,
        },
        hasSupport: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        priceFormat,
        goToStep(step) {
            if (step === 1) {
                Analytics.pushToDataLayer({
                    event: 'click',
                    attributes: {
                        section: 'Plan Overview',
                        action: 'Click button',
                        event_label: 'Go to payment',
                    },
                });
            }
            this.currentStep = step;
        },
        tempStoreItems(items) {
            items = items.filter(item => {
                if (!!item.metadata.showForCountries) {
                    if (
                        this.currency != 'USD'
                        || !item.metadata.showForCountries.includes(this.country.toLowerCase())
                    ) {
                        return true;
                    }
                }

                return false;
            });

            return items;
        },
        isAttachment(itemId) {
            return !this.$store.state.cartInfo.pricing.plans.find(plan => plan.id === itemId);
        },
        findItem(itemId) {
            let foundItem;
            if (this.isAttachment(itemId)) {
                const attachments = [
                    ...this.$store.getters.selectedPlan.addons,
                    ...this.$store.getters.selectedPlan.charges,
                ];
                foundItem = attachments.find(attachment => attachment.itemId === itemId);
            } else {
                foundItem = this.$store.state.cartInfo.pricing.plans.find(plan => plan.id == itemId);
            }

            return foundItem;
        },
    },
    computed: {
        installmentBreakdown() {
            let countArray = [];
            for (let i = 0; i < this.$store.getters.cartInfo.installments.count; i++) {

                if (i + 1 === this.$store.getters.cartInfo.installments.count) {
                    countArray.push(`and ${i + 1}`);
                } else {
                    countArray.push(i + 1);
                }
            }

            return countArray.join(', ');
        },
        appliedPromos() {
            let total = this.$store.getters.totals.invoiceDiscountTotal.total + this.$store.getters.totals.limitedPeriodDiscountTotal + this.$store.getters.totals.oneTimeDiscountTotal + this.$store.getters.totals.everGreenDiscountTotal;

            if (this.$store.getters.featureFlags?.BN_PULL_TOTALS_FROM_CB_ESTIMATE && this.$store.getters.estimate.discount > 0) {
                total = this.$store.getters.estimate.discount / 100;
            }

            if (total <= 0) {
                return false;
            }

            return `-${this.$store.getters.currencyPrefix}${priceFormat(total)}`;
        },
        canSubmit() {
            return !this.$store.getters.isSoftLoading && this.$store.getters.canSubmit;
        },
        chargedTodayTotal() {
            let total = (this.$store.getters.totals.total - this.$store.getters.totals.invoiceDiscountTotal.total) / (this.$store.getters.cartInfo.installments.count || 1);

            if (this.$store.getters.featureFlags?.BN_PULL_TOTALS_FROM_CB_ESTIMATE) {
                total = this.$store.getters.estimate.total / 100;

                if (this.$store.getters.cartInfo.installments.enabled) {
                    total = total / this.$store.getters.cartInfo.installments.count;
                }
            }

            return total < 0 ? priceFormat(0) : priceFormat(total);
        },
        subtotalInvoiceDue() {
            let total = this.$store.getters.totals.subTotal + this.$store.getters.totals.oneTimeChargesTotal;

            return total < 0 ? priceFormat(0) : priceFormat(total);
        },
        totalInvoiceDue() {
            let total = this.$store.getters.totals.total - this.$store.getters.totals.invoiceDiscountTotal.total;

            if (this.$store.getters.featureFlags?.BN_PULL_TOTALS_FROM_CB_ESTIMATE && this.$store.getters.hasFetchedEstimate) {
                total = this.$store.getters.estimate.total / 100;
            }

            return total < 0 ? priceFormat(0) : priceFormat(total);
        },
        renewalTotal() {
            let total = this.$store.getters.totals.subTotal - this.$store.getters.totals.everGreenDiscountTotal - this.$store.getters.totals.invoiceDiscountTotal.everGreenTotal;

            if (this.$store.getters.featureFlags?.BN_PULL_TOTALS_FROM_CB_ESTIMATE) {
                total = this.$store.getters.estimate?.renewal?.subtotal / 100;
            }

            return total < 0 ? priceFormat(0) : priceFormat(total);
        },
        invoiceCoupons() {
            let coupons = this.$store.getters.invoiceCoupons;

            coupons = coupons.map(coupon => {
                let description = '';
                let calcAmount = 0;

                if (coupon.duration == 'limited_period') {
                    if (coupon.type === 'fixed_amount') {
                        calcAmount = coupon.amount / 100;
                        description = `${this.$store.getters.currencyPrefix}${coupon.amount / 100} off for ${coupon.period} ${pluralize(coupon.periodUnit, coupon.period)}`;
                    }
                    if (coupon.type === 'percentage') {
                        calcAmount = priceFormat(this.$store.getters.totals.subTotal * ((coupon.amount / 100) / 100));
                        description = `${coupon.amount / 100}% off for ${coupon.period} ${pluralize(coupon.periodUnit, coupon.period)}`;
                    }
                }

                if (coupon.duration == 'one_time') {
                    if (coupon.type === 'fixed_amount') {
                        calcAmount = coupon.amount / 100;
                        description = `${this.$store.getters.currencyPrefix}${coupon.amount / 100} off one time`;
                    }

                    if (coupon.type === 'percentage') {
                        calcAmount = priceFormat(this.$store.getters.totals.subTotal * ((coupon.amount / 100) / 100));
                        description = `${coupon.amount / 100}% off one time`;
                    }
                }

                if (coupon.duration == 'forever') {
                    if (coupon.type === 'fixed_amount') {
                        calcAmount = coupon.amount / 100;
                        description = `${this.$store.getters.currencyPrefix}${coupon.amount / 100} off forever`;
                    }

                    if (coupon.type === 'percentage') {
                        calcAmount = priceFormat(this.$store.getters.totals.total * ((coupon.amount / 100) / 100));
                        description = `${coupon.amount / 100}% off forever`;
                    }
                }

                return {
                    ...coupon,
                    description,
                    calcAmount,
                };
            });

            return coupons;
        },
        planItem() {
            const foundItem = {
                ...this.findItem(this.$store.getters.selectedPlanId),
                itemId: this.$store.getters.selectedPlanId,
                selectedQuantity: 1,
            };

            return foundItem;
        },
        addonLineItems() {
            const items = this.$store.state.cartInfo.selectedAddons;
            const fullItems = items.map(item => {
                const foundItem = this.findItem(item.itemId);
                if (!foundItem) {
                    return;
                }
                return {
                    ...foundItem,
                    selectedQuantity: item.quantity,
                };
            }).filter(item => !!item);

            return fullItems.map(item => {
                return {
                    ...item,
                    ...(!item.itemId && {itemId: item.id})
                }
            }).sort((a, b) => {
                return a.metadata?.order - b.metadata?.order;
            });
        },
        chargeLineItems() {
            const items = this.$store.state.cartInfo.selectedCharges;
            const fullItems = items.map(item => {
                const foundItem = this.findItem(item.itemId);
                if (!foundItem) {
                    return;
                }
                return {
                    ...foundItem,
                    selectedQuantity: item.quantity,
                };
            }).filter(item => !!item).map(item => {
                if (item.itemId.toLowerCase().includes('installment-charge')) {
                    item.customDescription = `<p class="text-green-accessible">${this.$store.getters.cartInfo.installments.count} monthly payments</p>`;
                }
                return {
                    ...item,
                    ...(!item.itemId && {itemId: item.id})
                }
            }).sort((a, b) => {
                return a.metadata.order - b.metadata.order;
            });

            return fullItems;
        },
        currencies() {
            return this.$store.getters.allCurrencies;
        },
        country() {
            return this.$store.state.userInfo.country;
        },
        tcAgreement: {
            get() {
                return this.$store.getters.cartInfo.acceptedTc;
            },
            set(value) {
                this.$store.commit('updateCartInfo', { acceptedTc: value })
            }
        },
        tcAgreementValid: {
            get() {
                return this.$store.state.validation.tcAgreement.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { tcAgreement: { valid: value, message: this.tcAgreementError }})
            }
        },
        tcAgreementError: {
            get() {
                return this.$store.state.validation.tcAgreement.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { tcAgreement: { valid: this.tcAgreementValid, message: value }})
            }
        },
        mbg90Agreement: {
            get() {
                return this.$store.getters.cartInfo.acceptedMbg90;
            },
            set(value) {
                this.$store.commit('updateCartInfo', { acceptedMbg90: value })
            }
        },
        mbg90AgreementValid: {
            get() {
                return this.$store.state.validation.mbg90Agreement.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { mbg90Agreement: { valid: value, message: this.mbg90AgreementError }})
            }
        },
        mbg90AgreementError: {
            get() {
                return this.$store.state.validation.mbg90Agreement.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { mbg90Agreement: { valid: this.mbg90AgreementValid, message: value }})
            }
        },
        currency: {
            get() {
                return this.$store.getters.selectedCurrency;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { currency: value })
            }
        },
        currentStep: {
            get() {
                return this.$store.getters.currentStep;
            },
            set( step ) {
                this.$store.commit('setCurrentStep', step);
            }
        }
    },
    watch: {
        tcAgreement(newVal, oldVal) {
            if (!newVal) {
                this.tcAgreementValid = false;
            } else {
                this.tcAgreementValid = true;
            }
        },
        mbg90Agreement(newVal, oldVal) {
            if (!newVal) {
                this.mbg90AgreementValid = false;
            } else {
                this.mbg90AgreementValid = true;
            }
        },
        country(newVal) {
            let itemsToStore = this.tempStoreItems([...this.addonLineItems, ...this.chargeLineItems]);

            if (itemsToStore.length <= 0 && this.$store.getters.tempStorage.attachments.length > 0 && (['us', 'ca'].includes(newVal.toLowerCase()) || this.currency === 'USD')) {
                this.$store.getters.tempStorage.attachments.forEach(item => {
                    this.$store.commit('addAttachment', { itemId: item.itemId, quantity: item.quantity });
                });
                this.$store.commit('clearTempAttachmentStorage');
            } else {
                itemsToStore.forEach(item => {
                    this.$store.commit('updateTempAttachmentStorage', {itemId: item.itemId, quantity: item.selectedQuantity});
                    this.$store.commit('removeAttachment', item.itemId);
                });
            }
        },
        currency(newVal) {
            let itemsToStore = this.tempStoreItems([...this.addonLineItems, ...this.chargeLineItems]);

            Analytics.pushToDataLayer({
                event: 'click',
                attributes: {
                    section: 'Plan Overview',
                    action: 'Changed currency',
                    event_label: newVal,
                },
            });

            if (itemsToStore.length <= 0 && this.$store.getters.tempStorage.attachments.length > 0 && newVal == 'USD') {
                this.$store.getters.tempStorage.attachments.forEach(item => {
                    this.$store.commit('addAttachment', { itemId: item.itemId, quantity: item.quantity });
                });
                this.$store.commit('clearTempAttachmentStorage');
            } else {
                itemsToStore.forEach(item => {
                    this.$store.commit('updateTempAttachmentStorage', {itemId: item.itemId, quantity: item.selectedQuantity});
                    this.$store.commit('removeAttachment', item.itemId);
                });
            }
        }
    }
};
</script>

<style lang="postcss" scoped>
</style>
